import type { SceneTracks, TimelineTrack } from "@/timeline";
import type { MediaAsset } from "@/media/types";
import { RootNode } from "./nodes/root-node";
import { VideoNode } from "./nodes/video-node";
import { ImageNode } from "./nodes/image-node";
import { TextNode } from "./nodes/text-node";
import { StickerNode } from "./nodes/sticker-node";
import { GraphicNode } from "./nodes/graphic-node";
import { ColorNode } from "./nodes/color-node";
import { BlurBackgroundNode } from "./nodes/blur-background-node";
import { BackgroundImageNode } from "./nodes/background-image-node";
import { getCustomBackgroundUrl } from "@/background/custom-backgrounds-registry";
import { getPresetLoopAsset } from "@/presets/preset-loop-registry";
import { LoopBackgroundNode } from "./nodes/loop-background-node";
import { EffectLayerNode } from "./nodes/effect-layer-node";
import type { AnyBaseNode } from "./nodes/base-node";
import type { TBackground, TCanvasSize, TLoop } from "@/project/types";
import { DEFAULT_BACKGROUND_BLUR_INTENSITY } from "@/background/blur";
import {
	buildTransformFromParams,
	readBlendModeFromParams,
	readOpacityFromParams,
} from "@/rendering";

const PREVIEW_MAX_IMAGE_SIZE = 2048;

function getVisibleSortedElements({ track }: { track: TimelineTrack }) {
	return track.elements
		.filter((element) => !("hidden" in element && element.hidden))
		.slice()
		.sort((a, b) => {
			if (a.startTime !== b.startTime) return a.startTime - b.startTime;
			return a.id.localeCompare(b.id);
		});
}

function buildTrackNodes({
	tracks,
	mediaMap,
	canvasSize,
	isPreview,
}: {
	tracks: TimelineTrack[];
	mediaMap: Map<string, MediaAsset>;
	canvasSize: TCanvasSize;
	isPreview?: boolean;
}): AnyBaseNode[] {
	const nodes: AnyBaseNode[] = [];

	for (const track of tracks) {
		const elements = getVisibleSortedElements({ track });

		for (const element of elements) {
			if (element.type === "effect") {
				nodes.push(
					new EffectLayerNode({
						effectType: element.effectType,
						effectParams: element.params,
						timeOffset: element.startTime,
						duration: element.duration,
					}),
				);
				continue;
			}

			if (element.type === "video" || element.type === "image") {
				const mediaAsset = mediaMap.get(element.mediaId);
				if (!mediaAsset?.file || !mediaAsset?.url) {
					continue;
				}

				if (element.type === "video" && mediaAsset.type === "video") {
					nodes.push(
						new VideoNode({
							mediaId: mediaAsset.id,
							url: mediaAsset.url,
							file: mediaAsset.file,
							duration: element.duration,
							timeOffset: element.startTime,
							trimStart: element.trimStart,
							trimEnd: element.trimEnd,
							retime: element.retime,
							transform: buildTransformFromParams({ params: element.params }),
							animations: element.animations,
							opacity: readOpacityFromParams({ params: element.params }),
							blendMode: readBlendModeFromParams({ params: element.params }),
							effects: element.effects ?? [],
							masks: element.masks ?? [],
						}),
					);
				}
				if (element.type === "image" && mediaAsset.type === "image") {
					nodes.push(
						new ImageNode({
							url: mediaAsset.url,
							duration: element.duration,
							timeOffset: element.startTime,
							trimStart: element.trimStart,
							trimEnd: element.trimEnd,
							transform: buildTransformFromParams({ params: element.params }),
							animations: element.animations,
							opacity: readOpacityFromParams({ params: element.params }),
							blendMode: readBlendModeFromParams({ params: element.params }),
							effects: element.effects ?? [],
							masks: element.masks ?? [],
							...(isPreview && {
								maxSourceSize: PREVIEW_MAX_IMAGE_SIZE,
							}),
						}),
					);
				}
			}

			if (element.type === "text") {
				nodes.push(
					new TextNode({
						...element,
						transform: buildTransformFromParams({ params: element.params }),
						opacity: readOpacityFromParams({ params: element.params }),
						blendMode: readBlendModeFromParams({ params: element.params }),
						canvasCenter: { x: canvasSize.width / 2, y: canvasSize.height / 2 },
						canvasHeight: canvasSize.height,
						textBaseline: "middle",
						effects: element.effects ?? [],
					}),
				);
			}

			if (element.type === "sticker") {
				nodes.push(
					new StickerNode({
						stickerId: element.stickerId,
						intrinsicWidth: element.intrinsicWidth,
						intrinsicHeight: element.intrinsicHeight,
						duration: element.duration,
						timeOffset: element.startTime,
						trimStart: element.trimStart,
						trimEnd: element.trimEnd,
						transform: buildTransformFromParams({ params: element.params }),
						animations: element.animations,
						opacity: readOpacityFromParams({ params: element.params }),
						blendMode: readBlendModeFromParams({ params: element.params }),
						effects: element.effects ?? [],
					}),
				);
			}

			if (element.type === "graphic") {
				nodes.push(
					new GraphicNode({
						definitionId: element.definitionId,
						params: element.params,
						duration: element.duration,
						timeOffset: element.startTime,
						trimStart: element.trimStart,
						trimEnd: element.trimEnd,
						transform: buildTransformFromParams({ params: element.params }),
						animations: element.animations,
						opacity: readOpacityFromParams({ params: element.params }),
						blendMode: readBlendModeFromParams({ params: element.params }),
						effects: element.effects ?? [],
						masks: element.masks ?? [],
					}),
				);
			}
		}
	}

	return nodes;
}

type BlurEligibleMediaAsset = Omit<MediaAsset, "url" | "file" | "type"> & {
	url: string;
	file: File;
	type: "video" | "image";
};

function resolveBlurEligibleMediaAsset({
	element,
	mediaMap,
}: {
	element: ReturnType<typeof getVisibleSortedElements>[number];
	mediaMap: Map<string, MediaAsset>;
}): BlurEligibleMediaAsset | undefined {
	if (element.type !== "video" && element.type !== "image") {
		return undefined;
	}
	// A banner (see VideoElement/ImageElement.sourceBannerId) is an overlay meant to sit visibly
	// on top of everything — never the "main content" a blurred backdrop should be derived from.
	// Without this exclusion, a banner (always inserted at overlay index 0, the front-most/first
	// track — see banners/components/banners-view.tsx) would win findBlurBackgroundSourceTrack's
	// search below whenever the main track is empty/hidden, silently blurring the banner itself
	// as the whole video's background instead of the actual footage.
	if (element.sourceBannerId) {
		return undefined;
	}

	const mediaAsset = mediaMap.get(element.mediaId);
	if (
		!mediaAsset?.file ||
		!mediaAsset?.url ||
		(mediaAsset.type !== "video" && mediaAsset.type !== "image")
	) {
		return undefined;
	}

	// Narrowed above (file/url truthy-checked); MediaAsset's own `url`/`file` are optional in its
	// type because not every MediaAsset kind requires them, not because they can be empty strings.
	return mediaAsset as BlurEligibleMediaAsset;
}

// Prefers the main track (the conventional single "primary content" track), but falls back to
// the topmost visible overlay track that actually has a blur-eligible (video/image) element — a
// clip can easily end up on an overlay track instead of main (drag/drop, multi-track edits, etc.),
// and without this fallback the background-blur branch below silently adds zero nodes when that
// happens: no error, no fallback fill, just an unblurred/empty backdrop. That's indistinguishable
// from "blur stopped working" even though nothing is actually broken about the blur itself.
function findBlurBackgroundSourceTrack({
	tracks,
	mediaMap,
}: {
	tracks: SceneTracks;
	mediaMap: Map<string, MediaAsset>;
}): TimelineTrack | undefined {
	const candidateTracks: TimelineTrack[] = [
		...(!tracks.main.hidden ? [tracks.main] : []),
		...tracks.overlay.filter((track) => !("hidden" in track && track.hidden)),
	];

	return candidateTracks.find((track) =>
		getVisibleSortedElements({ track }).some((element) =>
			resolveBlurEligibleMediaAsset({ element, mediaMap }),
		),
	);
}

function buildBlurBackgroundNodes({
	tracks,
	mediaMap,
	blurIntensity,
}: {
	tracks: SceneTracks;
	mediaMap: Map<string, MediaAsset>;
	blurIntensity: number;
}): AnyBaseNode[] {
	const sourceTrack = findBlurBackgroundSourceTrack({ tracks, mediaMap });
	if (!sourceTrack) {
		return [];
	}

	const nodes: AnyBaseNode[] = [];
	const elements = getVisibleSortedElements({ track: sourceTrack });

	for (const element of elements) {
		const mediaAsset = resolveBlurEligibleMediaAsset({ element, mediaMap });
		if (!mediaAsset || (element.type !== "video" && element.type !== "image")) {
			continue;
		}

		nodes.push(
			new BlurBackgroundNode({
				mediaId: mediaAsset.id,
				url: mediaAsset.url,
				file: mediaAsset.file,
				mediaType: mediaAsset.type,
				duration: element.duration,
				timeOffset: element.startTime,
				trimStart: element.trimStart,
				trimEnd: element.trimEnd,
				retime: element.type === "video" ? element.retime : undefined,
				blurIntensity,
			}),
		);
	}

	return nodes;
}

export type BuildSceneParams = {
	canvasSize: TCanvasSize;
	tracks: SceneTracks;
	mediaAssets: MediaAsset[];
	duration: number;
	background: TBackground;
	/** Optional — omitted call sites (e.g. project-manager.ts's thumbnail snapshot) simply render
	 * without a loop layer, same "fail soft" spirit as a missing/deleted background. */
	loop?: TLoop | null;
	isPreview?: boolean;
};

export function buildScene({
	canvasSize,
	tracks,
	mediaAssets,
	duration,
	background,
	loop,
	isPreview,
}: BuildSceneParams) {
	const rootNode = new RootNode({ duration });
	const mediaMap = new Map(mediaAssets.map((m) => [m.id, m]));

	const visibleTracks = [
		...tracks.overlay.filter((track) => !("hidden" in track && track.hidden)),
		...(!tracks.main.hidden ? [tracks.main] : []),
	];
	const orderedTracksBottomToTop = visibleTracks.slice().reverse();

	const allNodes = buildTrackNodes({
		tracks: orderedTracksBottomToTop,
		mediaMap,
		canvasSize,
		isPreview,
	});

	if (background.type === "blur") {
		const blurNodes = buildBlurBackgroundNodes({
			tracks,
			mediaMap,
			blurIntensity:
				background.blurIntensity ?? DEFAULT_BACKGROUND_BLUR_INTENSITY,
		});
		for (const node of blurNodes) {
			rootNode.add(node);
		}
	} else if (
		background.type === "color" &&
		background.color !== "transparent"
	) {
		rootNode.add(new ColorNode({ color: background.color }));
	} else if (background.type === "image") {
		// Resolved synchronously from the session-lifetime registry populated during
		// EditorProvider's boot (custom-backgrounds-registry.ts) — buildScene itself can't be
		// async, and the background may reference an upload from a different project/session than
		// the one that created it, so there's nothing else here to resolve it from. If the
		// registry hasn't loaded yet or the background was since deleted, this quietly renders
		// nothing rather than throwing — same "fail soft" spirit as the color/blur branches above.
		const url = getCustomBackgroundUrl(background.backgroundId);
		if (url) {
			rootNode.add(new BackgroundImageNode({ url }));
		}
	}

	// A decorative looping layer (presets/), independent of — and always drawn ON TOP of — the
	// background above, but still BEFORE every track's own content, so it reads as "behind the
	// user's footage" regardless of which track that footage happens to be on. Same fail-soft
	// "resolve from the session registry, render nothing if missing/deleted" pattern as the
	// image-background branch just above.
	if (loop) {
		const asset = getPresetLoopAsset(loop.presetId);
		if (asset) {
			rootNode.add(
				new LoopBackgroundNode({
					mediaId: `loop:${loop.presetId}`,
					url: asset.url,
					file: asset.file,
					mediaType: asset.mediaType,
					durationTicks: asset.durationTicks,
				}),
			);
		}
	}

	for (const node of allNodes) {
		rootNode.add(node);
	}

	return rootNode;
}
